import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox, FormControlLabel, Paper, Typography, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addDays, format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { Controller } from 'react-hook-form';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';
import SelectField from '@/components/SelectField';
import { Destination } from '@/types/parcel/createOrder/create.interface';
import { NearestPoint } from '@/types/parcel/createOrder/p99.interface';
import { DeliveryTypes } from '@/types/parcel/createOrder/quotation.interface';

import type { CreateOrderFormType, CreateOrderFormTypeKeys } from '../create-order-schema';

import AdditionalOptions from '../dependencies/AdditionalOptions';
import {
  getAdditionalOptions,
  iconProducts,
  iconSize,
  pickupOptions,
  recipientData,
  senderData,
} from '../dependencies/constants';
import DestinationAddressSection from '../dependencies/destination-address-section';
import DropoffTitle from '../dependencies/DropoffTitle';
import OriginAddressSection from '../dependencies/origin-address-section';
import PickupTitle from '../dependencies/PickupTitle';
import SavedAddressesModal from '../dependencies/SavedAddressesModal/saved-addresses-modal';
import SelectorSection from '../dependencies/selector-section';
import StandardTitle from '../dependencies/StandardTitle';
import UserDataBox from '../dependencies/UserDataBox';
import VolumeCalculator from '../dependencies/VolumeCalculator';
import useCreateOrderForm from './use-create-order-form';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid',
    borderColor: theme.palette.primary.main,
  },
}));

interface CreateOrderFormProps {
  getQuotation: (value: CreateOrderFormType) => Promise<void>;
  resetQuotation?: () => void;
  resetForm: boolean;
  loading: boolean;
  handleHasQuot: () => void;
  getP99: ({ latitude, longitude }: { latitude?: number; longitude?: number }) => Promise<void>;
  p99Selected?: NearestPoint;
  handleCoordinatesPoint?: (values: Destination, type: 'origin' | 'destination') => void;
  quotData?: DeliveryTypes[] | null;
  onFormChange?: (values: CreateOrderFormType) => void;
}

const CreateOrderForm = (props: CreateOrderFormProps) => {
  const {
    getQuotation,
    resetQuotation,
    resetForm,
    loading,
    handleHasQuot,
    getP99,
    handleCoordinatesPoint,
    quotData,
    onFormChange,
  } = props;

  const hooks = useCreateOrderForm({
    resetForm,
    handleHasQuot,
    getP99,
    handleCoordinatesPoint,
    getQuotation,
    resetQuotation,
    quotData,
    loadingQuotation: loading,
    onFormChange,
  });
  const theme = useTheme();
  const additionalOptions = getAdditionalOptions(hooks.insurancePercentage);

  return (
    <form
      className="space-y-4"
      autoComplete="off"
      role="presentation"
      data-form-type="other"
      data-lpignore="true"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Dirección origen */}
      <Paper variant="outlined" className="p-6 space-y-6">
        <PickupTitle />
        <OriginAddressSection
          control={hooks.control}
          register={hooks.register}
          errors={hooks.errors}
          setValue={hooks.setValue}
          watch={hooks.watch}
          geoSuggestRef={hooks.geoSuggestRef}
          userCountry={hooks.userCountry}
          showDirectionForm={hooks.showDirectionForm}
          showZipCodeInput={hooks.showZipCodeInput}
          findAddressComponent={hooks.findAddressComponent}
          handleZipCodeNotFound={hooks.handleZipCodeNotFound}
          handleShowDirectionForm={hooks.handleShowDirectionForm}
          setProfileData={hooks.setProfileData}
          onOpenSavedAddresses={() => hooks.handleOpenSavedAddresses('origin')}
        />
        {/* Datos de usuario */}
        <UserDataBox
          control={hooks.control}
          handleChangeSelectedCountryPhone={hooks.handleChangeSelectedCountryPhone}
          userData={senderData}
          register={hooks.register}
          errors={hooks.errors}
          watch={hooks.watch}
        />

        {/* Casilla para guardar dirección de recolección y contacto */}
        <div className="pt-3 border-t border-gray-100 flex flex-col gap-0.5">
          <FormControlLabel
            control={
              <Checkbox
                checked={!!hooks.watch('saveOriginAddress')}
                onChange={(e) => hooks.handleToggleSaveOriginAddress(e.target.checked)}
                color="primary"
                size="small"
                className="!p-1"
              />
            }
            label="Guardar esta dirección de recolección y contacto para futuros envíos"
            sx={{ '&& span': { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' } }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 3.5 }}>
            Almacena la dirección, referencias y datos del remitente en tu libreta de direcciones
            frecuentes.
          </Typography>
        </div>
      </Paper>

      {/* Dirección de destino */}
      <Paper variant="outlined" className="p-6 space-y-6">
        <DropoffTitle />
        <DestinationAddressSection
          control={hooks.control}
          register={hooks.register}
          errors={hooks.errors}
          setValue={hooks.setValue}
          watch={hooks.watch}
          geoSuggestRef={hooks.geoSuggestDestinationRef}
          userCountry={hooks.userCountry}
          showDirectionForm={hooks.showDirectionForm}
          showZipCodeInput={hooks.showZipCodeInput}
          findAddressComponent={hooks.findAddressComponent}
          handleZipCodeNotFound={hooks.handleZipCodeNotFound}
          handleShowDirectionForm={hooks.handleShowDirectionForm}
          onOpenSavedAddresses={() => hooks.handleOpenSavedAddresses('destination')}
        />
        {/* Datos de usuario */}
        <UserDataBox
          control={hooks.control}
          handleChangeSelectedCountryPhone={hooks.handleChangeSelectedCountryPhone}
          userData={recipientData}
          register={hooks.register}
          errors={hooks.errors}
          watch={hooks.watch}
        />

        {/* Casilla para guardar destinatario y dirección */}
        <div className="pt-3 border-t border-gray-100 flex flex-col gap-0.5">
          <FormControlLabel
            control={
              <Checkbox
                checked={!!hooks.watch('saveDestinationAddress')}
                onChange={(e) => hooks.handleToggleSaveDestinationAddress(e.target.checked)}
                color="primary"
                size="small"
                className="!p-1"
              />
            }
            label="Guardar este destinatario y dirección para futuros envíos"
            sx={{ '&& span': { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' } }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 3.5 }}>
            Almacena la dirección de entrega, coordenadas y datos de contacto de este destinatario
            en tu libreta.
          </Typography>
        </div>
      </Paper>

      {/* Tamaño de paquete */}
      <Paper variant="outlined" className="p-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center flex-nowrap gap-1">
            <StandardTitle title="¿Cuál es el peso total del paquete que necesitas enviar?" />
            <HtmlTooltip placement="top" title="Calculá el volúmen de tu paquete">
              <IconButton onClick={hooks.handleOpenCloseSizeCalculator} className="!p-1">
                <HelpOutlineIcon color="disabled" fontSize="small" />
              </IconButton>
            </HtmlTooltip>
          </div>
          <SelectorSection
            options={hooks.sizeChips}
            handleClick={hooks.selectSize}
            iconFunc={iconSize}
            optionSelected={hooks.watch('items.size')}
          />
        </div>
      </Paper>

      {/* Tipo de envío */}
      <Paper variant="outlined" className="p-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <StandardTitle title="Selecciona un tipo de envío" />
            {hooks.loadingQuotation && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                <CircularProgress size={14} color="primary" />
                <span>Actualizando tarifas...</span>
              </div>
            )}
          </div>

          <SelectorSection
            options={hooks.filterDeliveryTypeChips}
            handleClick={hooks.selectDeliveryType}
            iconFunc={iconProducts}
            optionSelected={hooks.watch('deliveryType') as string}
          />

          {/* Alerta respetuosa si el servicio seleccionado no cuenta con cobertura para la ruta */}
          {hooks.isQuoted && !hooks.selectedTypeHasCoverage && (
            <Box
              sx={{
                backgroundColor: '#FFFDF0',
                border: '1px solid #FFEBAA',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 1,
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#B7791F' }} fontSize="small" />
              <Typography variant="body2" sx={{ color: '#8A6D3B', fontSize: '0.875rem' }}>
                El servicio seleccionado no cuenta con cobertura para esta ruta.
                {hooks.availableCoverageNames ? (
                  <>
                    {' '}
                    Opciones con cobertura disponibles:{' '}
                    <strong>{hooks.availableCoverageNames}</strong>.
                  </>
                ) : (
                  ' Por favor verifica las direcciones de origen y destino.'
                )}
              </Typography>
            </Box>
          )}
        </div>
      </Paper>

      {/* Adicionales */}
      <Paper variant="outlined" className="p-6">
        <Grid container size={{ xxs: 12 }} sx={{ display: 'flex', flexDirection: 'column' }}>
          <StandardTitle title="Adicionales" />
          {additionalOptions.map((item) => (
            <AdditionalOptions
              key={item.checkName}
              register={hooks.register}
              errors={hooks.errors}
              {...item}
              isChecked={!!hooks.watch(item.checkName as CreateOrderFormTypeKeys)}
              disabled={hooks.isNationalSelected}
              checkboxChange={() => {
                const value = hooks.watch(item.checkName as CreateOrderFormTypeKeys);
                hooks.setValue(item.checkName as CreateOrderFormTypeKeys, !value);

                if (value) {
                  hooks.setValue(item.inputName as CreateOrderFormTypeKeys, undefined);
                  hooks.unregister(item.inputName as CreateOrderFormTypeKeys);
                } else {
                  hooks.setValue(item.inputName as CreateOrderFormTypeKeys, 0);
                }
              }}
            />
          ))}
        </Grid>

        {/* ¿Cuándo prefieres la recolección de tu paquete? */}
        <Grid container size={{ xxs: 12 }}>
          <StandardTitle title="¿Cuándo prefieres la recolección de tu paquete?" />
          <Controller
            name="options.scheduleNow"
            control={hooks.control}
            render={({ field }) => (
              <SelectField
                label=""
                errors={hooks.errors}
                options={pickupOptions.map((option) => ({
                  label: option.label,
                  value: option.value.toString(),
                }))}
                sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
                size="small"
                disabled={hooks.isNationalSelected}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  field.onChange(e.target.value === 'true');
                  if (e.target.value === 'true') {
                    hooks.setValue('draft', false);
                    hooks.setValue('options.pickUpAfter', '');
                  } else {
                    hooks.setValue('options.pickUpAfter', undefined);
                    hooks.unregister('options.pickUpAfter');
                    hooks.setValue('draft', true);
                  }
                }}
              />
            )}
          />
        </Grid>

        {/* Fecha de recolección */}
        {hooks.watch('options.scheduleNow') && (
          <Grid container size={{ xxs: 12 }} mt={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha de recolección"
                value={
                  hooks.watch('options.pickUpAfter')
                    ? parse(hooks.watch('options.pickUpAfter') ?? '', 'yyyy-MM-dd', new Date())
                    : null
                }
                onChange={(date) => {
                  if (date) {
                    hooks.setValue('options.pickUpAfter', format(date, 'yyyy-MM-dd'), {
                      shouldValidate: true,
                    });
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                  popper: {
                    placement: 'bottom-start',
                    disablePortal: true,
                  },
                }}
                sx={{ width: '100%' }}
                minDate={addDays(new Date(), 1)}
              />
            </LocalizationProvider>
          </Grid>
        )}

        <Grid container size={{ xxs: 12 }} mt={2}>
          <TextField
            {...hooks.register('internalKey')}
            value={hooks.watch('internalKey') || ''}
            size="small"
            variant="outlined"
            fullWidth
            label="Identificador de orden (opcional)"
            placeholder="Solo letras, números, guiones y guiones bajos (mín 5, máx 100)"
            slotProps={{
              htmlInput: {
                autoComplete: 'off',
                role: 'presentation',
                'data-form-type': 'other',
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
              },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <HtmlTooltip
                      placement="top"
                      title="Es tu número de referencia interno o identificador único para esta orden. Te servirá para rastrear, buscar o filtrar esta orden en tus reportes."
                    >
                      <IconButton size="small" className="!p-1">
                        <HelpOutlineIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                      </IconButton>
                    </HtmlTooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        <Modal
          title="Calculá el volúmen de tu paquete"
          isOpen={hooks.openSizeCalculator}
          onClose={hooks.handleOpenCloseSizeCalculator}
          actions={
            <Grid container size={12} justifyContent="end">
              <Button
                disabled={!hooks.isDisabled}
                onClick={() => {
                  hooks.selectSize(hooks.calculatedSize?.code || '');
                  hooks.handleOpenCloseSizeCalculator();
                }}
              >
                Seleccionar tamaño sugerido
              </Button>
            </Grid>
          }
        >
          <VolumeCalculator
            calculatedSize={hooks.calculatedSize}
            register={hooks.register}
            errors={hooks.errors}
          />
        </Modal>

        {hooks.savedAddressesModalState.open && (
          <SavedAddressesModal
            open={hooks.savedAddressesModalState.open}
            targetType={hooks.savedAddressesModalState.targetType}
            onClose={hooks.handleCloseSavedAddresses}
            onSelect={hooks.handleSelectSavedAddress}
            userCountry={hooks.userCountry}
          />
        )}
      </Paper>
    </form>
  );
};

export default CreateOrderForm;
